import { useEffect, useMemo, useState } from "react";
import type {
    DrinkBarStatusDto,
    DriverRequestDto,
    EventDto,
    EventFileSectionDto,
    MyShiftResponse,
    PersonDto,
    PersonFunctionDto,
    ShiftDto,
    TicketDto,
} from "../types";

type Params = {
    apiUrl?: string;
    eid: number;
    pid: number;
};

export function useEventPersonOverview({ apiUrl, eid, pid }: Params) {
    const [loading, setLoading] = useState(true);
    const [eventInfo, setEventInfo] = useState<EventDto | null>(null);
    const [personInfo, setPersonInfo] = useState<PersonDto | null>(null);
    const [fileSections, setFileSections] = useState<EventFileSectionDto[]>([]);
    const [shifts, setShifts] = useState<ShiftDto[]>([]);
    const [ticketInfo, setTicketInfo] = useState<TicketDto[]>([]);
    const [drinkBars, setDrinkBars] = useState<DrinkBarStatusDto[]>([]);
    const [canManageTransport, setCanManageTransport] = useState(false);
    const [driverRequests, setDriverRequests] = useState<DriverRequestDto[]>([]);
    const [personFunctions, setPersonFunctions] = useState<PersonFunctionDto[]>([]);
    const [hasVipParking, setHasVipParking] = useState(false);
    const [canSeeExperience, setCanSeeExperience] = useState(false);
    const [canSeeTransportByFunction, setCanSeeTransportByFunction] = useState(false);

    const shiftsSorted = useMemo(() => {
        return [...shifts].sort((a, b) => {
            const da = `${a.date} ${a.start}`;
            const db = `${b.date} ${b.start}`;
            return da.localeCompare(db);
        });
    }, [shifts]);

    const activeDriverRequests = useMemo(() => {
        return driverRequests.filter((r) => r.currentDriverStatusId !== 4);
    }, [driverRequests]);

    const activeDrinkBars = useMemo(() => {
        return drinkBars.filter((x) => Number(x.remainingDrinks) > 0);
    }, [drinkBars]);


    const validTickets = useMemo(() => {
        const now = new Date();

        return ticketInfo.filter((t) => {
            if (!t.ticketTimeEnd) return false;
            const end = new Date(t.ticketTimeEnd);
            return end >= now;
        });
    }, [ticketInfo]);

    const showShifts = shiftsSorted.length > 0;
    const showTicket = validTickets.length > 0;
    const showInfo = !!eventInfo?.infoText?.trim();
    const showTransport = canSeeTransportByFunction// || canManageTransport;
    const showDriverRides = activeDriverRequests.length > 0;
    const showDrinks = activeDrinkBars.length > 0;

    async function loadDriverRequests() {
        if (!apiUrl || !eid || !pid) return;

        try {
            const res = await fetch(
                `${apiUrl}/api/v2/driver-requests/transport/driver/my-requests?personId=${pid}&eventId=${eid}`
            );

            if (!res.ok) {
                setDriverRequests([]);
                return;
            }

            const data = await res.json();
            setDriverRequests(Array.isArray(data) ? data : []);
        } catch {
            setDriverRequests([]);
        }
    }

    async function addDriverLog(requestId: number, statusId: number, note?: string) {
        if (!apiUrl || !pid) return;

        try {
            const res = await fetch(
                `${apiUrl}/api/v2/driver-requests/transport/driver/requests/${requestId}/log`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        statusId,
                        driverId: pid,
                        note: note ?? null,
                    }),
                }
            );

            if (!res.ok) throw new Error("Kunne ikke opdatere driver-log");

            await loadDriverRequests();
        } catch (err) {
            console.error(err);
        }
    }

    async function loadAll() {
        if (!apiUrl || !eid || !pid || Number.isNaN(eid) || Number.isNaN(pid)) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const [
                canRes,
                personRes,
                eventRes,
                filesRes,
                shiftsRes,
                ticketRes,
                personFunctionsRes,
                parkingRes,
                drinkRes,
                overviewAccessRes
            ] = await Promise.allSettled([
                fetch(`${apiUrl}/api/transport/can-manage?eventId=${eid}&personId=${pid}`),
                fetch(`${apiUrl}/api/persons/public/${pid}?eventId=${eid}`),
                fetch(`${apiUrl}/api/events/${eid}/light`),
                fetch(`${apiUrl}/api/events/${eid}/file-sections?personId=${pid}`),
                fetch(`${apiUrl}/api/shifts/my?personId=${pid}&eventId=${eid}`),
                fetch(`${apiUrl}/api/tickets/my-ticket?personId=${pid}&eventId=${eid}`),
                fetch(`${apiUrl}/api/persons/${pid}/functions?eventId=${eid}`),
                fetch(`${apiUrl}/api/parking/exists?eventId=${eid}&personId=${pid}`),
                fetch(`${apiUrl}/api/drinks/status?personId=${pid}&eventId=${eid}`),
                fetch(`${apiUrl}/api/events/${eid}/person-overview/access?personId=${pid}`),
                
            ]);

            if (canRes.status === "fulfilled" && canRes.value.ok) {
                setCanManageTransport(!!(await canRes.value.json()));
            } else {
                setCanManageTransport(false);
            }

            if (personRes.status === "fulfilled" && personRes.value.ok) {
                const p = await personRes.value.json();
                setPersonInfo({
                    personId: p.personId ?? p.PersonId ?? pid,
                    firstName: p.firstName ?? p.FirstName ?? "",
                    lastName: p.lastName ?? p.LastName ?? "",
                });
            } else {
                setPersonInfo({ personId: pid, firstName: "", lastName: "" });
            }

            if (eventRes.status === "fulfilled" && eventRes.value.ok) {
                const e = await eventRes.value.json();
                setEventInfo({
                    eventId: e.eventId ?? e.EventId ?? e.EventID ?? eid,
                    eventName: e.eventName ?? e.EventName ?? e.name ?? e.Name ?? "Event",
                    infoText:
                        e.infoText ??
                        e.InfoText ??
                        e.personInfoText ??
                        e.PersonInfoText ??
                        "",
                });
            } else {
                setEventInfo({ eventId: eid, eventName: "Event", infoText: "" });
            }

            if (filesRes.status === "fulfilled" && filesRes.value.ok) {
                const filesJson = await filesRes.value.json();

                const mappedSections: EventFileSectionDto[] = (Array.isArray(filesJson) ? filesJson : []).map((section: any) => ({
                    eventFileSectionID: section.eventFileSectionID ?? section.EventFileSectionID,
                    eventID: section.eventID ?? section.EventID,
                    name: section.name ?? section.Name,
                    description: section.description ?? section.Description,
                    sortOrder: section.sortOrder ?? section.SortOrder ?? 0,
                    isActive: section.isActive ?? section.IsActive ?? true,
                    isPublic: section.isPublic ?? section.IsPublic ?? false,
                    files: ((section.files ?? section.Files ?? []) as any[]).map((file) => ({
                        fileID: file.fileID ?? file.FileID,
                        eventID: file.eventID ?? file.EventID,
                        eventFileSectionID: file.eventFileSectionID ?? file.EventFileSectionID,
                        name: file.name ?? file.Name,
                        originalFileName: file.originalFileName ?? file.OriginalFileName,
                        sizeBytes: file.sizeBytes ?? file.SizeBytes,
                        sortOrder: file.sortOrder ?? file.SortOrder ?? 0,
                        downloadUrl: file.downloadUrl ?? file.DownloadUrl,
                    })),
                }));

                setFileSections(
                    mappedSections
                        .filter((section) => section.isActive && section.files?.length > 0)
                        .sort((a, b) => a.sortOrder - b.sortOrder || a.eventFileSectionID - b.eventFileSectionID)
                        .map((section) => ({
                            ...section,
                            files: [...section.files].sort((a, b) => a.sortOrder - b.sortOrder || a.fileID - b.fileID),
                        }))
                );
            } else {
                setFileSections([]);
            }
            if (overviewAccessRes.status === "fulfilled" && overviewAccessRes.value.ok) {
                const access = await overviewAccessRes.value.json();

                setCanSeeExperience(!!access.canSeeExperience);
                setCanSeeTransportByFunction(!!access.canSeeTransport);
            } else {
                setCanSeeExperience(false);
                setCanSeeTransportByFunction(false);
            }

            if (shiftsRes.status === "fulfilled" && shiftsRes.value.ok) {
                const s = (await shiftsRes.value.json()) as MyShiftResponse;
                const confirmed = Array.isArray(s?.confirmed) ? s.confirmed : [];

                const mapped: ShiftDto[] = confirmed.map((x) => {
                    const startDate = x.start ? new Date(x.start) : null;
                    const endDate = x.end ? new Date(x.end) : null;

                    const validStart = startDate && !Number.isNaN(startDate.getTime());
                    const validEnd = endDate && !Number.isNaN(endDate.getTime());

                    return {
                        shiftId: x.shiftId,
                        title: x.title ?? "",
                        date: validStart ? startDate.toISOString().slice(0, 10) : "",
                        start: validStart
                            ? startDate.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })
                            : "",
                        end: validEnd
                            ? endDate.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })
                            : "",
                        status: x.status,
                        functionId: x.functionId,
                        functionName: x.functionName,
                        eventId: x.eventId,
                        endsOneHourAfterPlay: x.endsOneHourAfterPlay ?? false,
                    };
                });

                setShifts(mapped);
            } else {
                setShifts([]);
            }

            if (ticketRes.status === "fulfilled" && ticketRes.value.ok) {
                const data = await ticketRes.value.json();

                const mapped: TicketDto[] = (Array.isArray(data) ? data : []).map((t: any) => ({
                    hasTicket: t.hasTicket ?? true,
                    ticketID: t.ticketID,
                    ticketDescription: t.ticketDescription,
                    ticketTimeStart: t.ticketTimeStart,
                    ticketTimeEnd: t.ticketTimeEnd,
                    active: t.active,
                    isUsed: t.isUsed,
                    usedAt: t.usedAt,
                    sourceType: t.sourceType,
                }));

                setTicketInfo(mapped);
            } else {
                setTicketInfo([]);
            }

            if (personFunctionsRes.status === "fulfilled" && personFunctionsRes.value.ok) {
                const pf = await personFunctionsRes.value.json();

                const mappedFunctions: PersonFunctionDto[] = (Array.isArray(pf) ? pf : []).map((x: any) => ({
                    functionId:
                        x.functionId ??
                        x.FunctionId ??
                        x.functionID ??
                        x.FunctionID,

                    functionName:
                        x.functionName ??
                        x.FunctionName ??
                        x.name ??
                        x.Name ??
                        x.function?.functionName ??
                        x.Function?.FunctionName ??
                        x.function?.name ??
                        x.Function?.Name,

                    eventId:
                        x.eventId ??
                        x.EventId ??
                        x.eventID ??
                        x.EventID,
                }));

                setPersonFunctions(mappedFunctions);
            } else {
                setPersonFunctions([]);
            }

            if (parkingRes.status === "fulfilled" && parkingRes.value.ok) {
                setHasVipParking(!!(await parkingRes.value.json()));
            } else {
                setHasVipParking(false);
            }

            if (drinkRes.status === "fulfilled" && drinkRes.value.ok) {
                const drinkJson = await drinkRes.value.json();
                setDrinkBars(Array.isArray(drinkJson) ? drinkJson : []);
            } else {
                setDrinkBars([]);
            }

            await loadDriverRequests();
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, eid, pid]);

    return {
        loading,
        eventInfo,
        personInfo,
        fileSections,
        shiftsSorted,
        validTickets,
        activeDrinkBars,
        activeDriverRequests,
        canSeeExperience,
        hasVipParking,
        showShifts,
        showTicket,
        showInfo,
        showTransport,
        showDriverRides,
        showDrinks,
        personFunctions,
        addDriverLog,
    };
}